import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

// @dani need to spin these up inside the component now based on the what portcovs exist
type DigitalMonthlyReportPresetTab =
  | 'facilitySummary'
  | 'borrowingBase'
  | 'borrowingBaseReceivables'
  | 'borrowingBaseCash'
  | 'borrowingBaseFinalBorrowingBaseReport'
  | 'portfolioCovenant'
  | 'inputs';

export type DigitalMonthlyReportTab = DigitalMonthlyReportPresetTab | string;

const tabsToLabels: Record<DigitalMonthlyReportTab, string> = {
  facilitySummary: 'Facility Summary',
  borrowingBase: 'Borrowing Base',
  borrowingBaseReceivables: 'Receivables',
  borrowingBaseCash: 'Cash',
  borrowingBaseFinalBorrowingBaseReport: 'Borrowing Base Report',
  portfolioCovenant: 'Portfolio Covenant',
  inputs: 'Inputs',
};

const DigitalMonthlyReportSidebarMenuButton = ({
  tab,
  selectedTab,
  onClick,
}: {
  tab: DigitalMonthlyReportTab;
  selectedTab: DigitalMonthlyReportTab;
  onClick: () => void;
}) => {
  const isSelectedTab = useMemo(() => {
    return tab === selectedTab;
  }, [tab, selectedTab]);

  return (
    <Button
      width="100%"
      variant="ghost"
      backgroundColor={isSelectedTab ? colors.aurora[100] : undefined}
      fontSize="18px"
      fontWeight={600}
      onClick={onClick}
      p="8px 16px"
    >
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Text color={isSelectedTab ? 'white' : undefined}>
          {tabsToLabels[tab] ?? commonUtils.toTitleCase(tab)}
        </Text>
        {isSelectedTab && <Icon icon="chevron-right" color="white" />}
      </Flex>
    </Button>
  );
};

const DigitalMonthlyReportSidebarSubMenuButton = ({
  tab,
  selectedTab,
  onClick,
}: {
  tab: DigitalMonthlyReportTab;
  selectedTab: DigitalMonthlyReportTab;
  onClick: () => void;
}) => {
  const isSelectedTab = useMemo(() => {
    return tab === selectedTab;
  }, [tab, selectedTab]);

  return (
    <Button
      width="95%"
      variant="ghost"
      backgroundColor={isSelectedTab ? colors.aurora[100] : undefined}
      fontSize="14px"
      onClick={onClick}
      p="8px 16px"
      color={colors.black[70]}
      style={{ fontWeight: 400, marginLeft: '16px' }} // need to put this explicitly in style to overwrite inherited margin
    >
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Text
          color={isSelectedTab ? 'white' : undefined}
          fontWeight={isSelectedTab ? 600 : 400}
        >
          {tabsToLabels[tab] ?? commonUtils.toTitleCase(tab)}
        </Text>
        {isSelectedTab && <Icon icon="chevron-right" color="white" />}
      </Flex>
    </Button>
  );
};

type DigitalMonthlyReportSidebarProps = {
  selectedTab: DigitalMonthlyReportTab;
  setSelectedTab: React.Dispatch<React.SetStateAction<DigitalMonthlyReportTab>>;
  portfolioCovenantKeys: string[] | null;
  borrowingBaseReportId: string | null;
};
export const DigitalMonthlyReportSidebar = ({
  selectedTab,
  setSelectedTab,
  portfolioCovenantKeys,
  borrowingBaseReportId,
}: DigitalMonthlyReportSidebarProps) => {
  return (
    <VStack
      padding="24px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
      spacing="16px"
      width="100%"
      maxHeight="70vh"
      overflowY="scroll"
    >
      <DigitalMonthlyReportSidebarMenuButton
        tab="facilitySummary"
        selectedTab={selectedTab}
        onClick={() => setSelectedTab('facilitySummary')}
      />

      {/* Borrowing Base Group */}
      {borrowingBaseReportId && (
        <Box w="100%">
          <DigitalMonthlyReportSidebarMenuButton
            tab="borrowingBase"
            selectedTab={selectedTab}
            onClick={() => setSelectedTab('borrowingBase')}
          />
          <Box h="8px" />
          <DigitalMonthlyReportSidebarSubMenuButton
            tab="borrowingBaseReceivables"
            selectedTab={selectedTab}
            onClick={() => setSelectedTab('borrowingBaseReceivables')}
          />
          <DigitalMonthlyReportSidebarSubMenuButton
            tab="borrowingBaseCash"
            selectedTab={selectedTab}
            onClick={() => setSelectedTab('borrowingBaseCash')}
          />
          <DigitalMonthlyReportSidebarSubMenuButton
            tab="borrowingBaseFinalBorrowingBaseReport"
            selectedTab={selectedTab}
            onClick={() =>
              setSelectedTab('borrowingBaseFinalBorrowingBaseReport')
            }
          />
        </Box>
      )}

      {/* Portfolio Covenant Group */}
      {portfolioCovenantKeys && !!portfolioCovenantKeys.length && (
        <Box w="100%">
          <DigitalMonthlyReportSidebarMenuButton
            tab="portfolioCovenant"
            selectedTab={selectedTab}
            onClick={() => setSelectedTab('portfolioCovenant')}
          />
          <Box h="8px" />
          {portfolioCovenantKeys.map((covenantKey) => (
            <DigitalMonthlyReportSidebarSubMenuButton
              key={covenantKey}
              tab={covenantKey}
              selectedTab={selectedTab}
              onClick={() => setSelectedTab(covenantKey)}
            />
          ))}
        </Box>
      )}

      <DigitalMonthlyReportSidebarMenuButton
        tab="inputs"
        selectedTab={selectedTab}
        onClick={() => setSelectedTab('inputs')}
      />
    </VStack>
  );
};
